import type { SearchHouse } from 'lc-services/types'
import type { Bounds, QueryModelType } from '~/types/search/types'

export const useSearchSplitMap = () => {
  const { $lcServicesSearch } = useNuxtApp()
  const { searchController, searchPresenter } = $lcServicesSearch
  const { algoliaIds } = useAuth()
  const { currency } = useUser()

  const stateSearchSplitMap = useState<{
    coordinatesMap: {
      neLat: number
      neLng: number
      swLat: number
      swLng: number
    }
    housesStorage: SearchHouse[]
  }>('state-search-split-map', () => ({
    coordinatesMap: {
      neLat: 0,
      neLng: 0,
      swLat: 0,
      swLng: 0,
    },
    housesStorage: [],
  }))

  const housesStorage = computed(() => stateSearchSplitMap.value.housesStorage)

  const geographicAreaActive = computed(() =>
    Boolean(
      stateSearchSplitMap.value.coordinatesMap.neLat &&
        stateSearchSplitMap.value.coordinatesMap.neLng &&
        stateSearchSplitMap.value.coordinatesMap.swLat &&
        stateSearchSplitMap.value.coordinatesMap.swLng,
    ),
  )

  const bounds = computed<Bounds>(() => {
    return [
      stateSearchSplitMap.value.coordinatesMap.neLat,
      stateSearchSplitMap.value.coordinatesMap.neLng,
      stateSearchSplitMap.value.coordinatesMap.swLat,
      stateSearchSplitMap.value.coordinatesMap.swLng,
    ]
  })

  const setBounds = (bounds: Bounds) => {
    if (bounds.length !== 4) return
    stateSearchSplitMap.value.coordinatesMap = {
      neLat: bounds[0],
      neLng: bounds[1],
      swLat: bounds[2],
      swLng: bounds[3],
    }
  }

  const resetBounds = () => {
    stateSearchSplitMap.value.coordinatesMap = {
      neLat: 0,
      neLng: 0,
      swLat: 0,
      swLng: 0,
    }
  }

  const getHouseInformations = async ({
    houseId,
    searchHousesPayload,
  }: {
    houseId: string
    searchHousesPayload: QueryModelType
  }) => {
    await searchController.getHouseSplitMap(
      {
        mode: searchHousesPayload.mode,
        replica: searchHousesPayload.replica,
        objectID: houseId,
        period: searchHousesPayload.period,
        prices: searchHousesPayload.prices,
      },
      currency.value,
      algoliaIds.value,
      'web',
    )

    setHousesStorage(searchPresenter.vm.housesStorage)
  }

  const setHousesStorage = (houses: SearchHouse[]) => {
    const existingIds = new Set(housesStorage.value.map((house) => house.id))
    const newHouses = houses.filter((house) => !existingIds.has(house.id))

    stateSearchSplitMap.value.housesStorage = [
      ...housesStorage.value,
      ...newHouses,
    ]
  }

  const resetHousesStorage = () => {
    stateSearchSplitMap.value.housesStorage = []
  }

  const onMapMoved = (bounds: Bounds, callback: () => void) => {
    setBounds(bounds)

    callback()
  }

  return {
    bounds,
    geographicAreaActive,
    getHouseInformations,
    housesStorage,
    onMapMoved,
    resetBounds,
    resetHousesStorage,
    setBounds,
    setHousesStorage,
  }
}
